import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { useUser } from '../../features/auth/useUser';
import { Button } from '../../components/common/Button';
import { Label } from '../../components/common/Label';
import { brandConfig } from '../../config/brands';
import { apiClient } from '../../services/apiClient';
import './ConnectionsPage.css';
import '../amazon/AmazonAuthPage.css';

interface GoogleAdsCredentialStatus {
  connected: boolean;
}

interface AmazonAccount {
  id: string;
}

interface AmazonAdsAccountsResponse {
  accounts: AmazonAccount[];
}

export default function ConnectionsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useUser();

  const googleAdsQuery = useQuery<GoogleAdsCredentialStatus>({
    queryKey: ['connections-google-ads-credentials'],
    queryFn: async () => {
      const res = await apiClient.get<GoogleAdsCredentialStatus>(
        '/ad-connect/credentials/google_ads',
      );
      return res.data;
    },
  });

  // Amazon Seller Partner API の連携状態は /amazon/accounts の件数で判定
  const amazonSellerQuery = useQuery<AmazonAccount[]>({
    queryKey: ['connections-amazon-accounts'],
    queryFn: async () => {
      const res = await apiClient.get<AmazonAccount[]>('/amazon/accounts');
      return res.data;
    },
    enabled: brandConfig.features.amazonIntegration,
  });

  // Amazon 広告 API の連携状態は /amazon-ads/accounts の件数で判定
  const amazonAdsQuery = useQuery<AmazonAdsAccountsResponse>({
    queryKey: ['connections-amazon-ads-accounts'],
    queryFn: async () => {
      const res = await apiClient.get<AmazonAdsAccountsResponse>('/amazon-ads/accounts');
      return res.data;
    },
    enabled: brandConfig.features.amazonIntegration,
  });

  const amazonSellerConnected = (amazonSellerQuery.data?.length ?? 0) > 0;
  const amazonAdsConnected = (amazonAdsQuery.data?.accounts?.length ?? 0) > 0;

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/charger_1.svg" alt="" className="page-title-icon" />
            接続管理
          </h1>
          <p className="page-description">
            外部サービスとのAPI連携を管理します
          </p>
        </div>
      </div>

      {!isAdmin && (
        <div className="readonly-banner" role="status">
          <span className="readonly-banner-icon">
            <Eye size={18} aria-hidden="true" />
          </span>
          <span>閲覧のみ可能です。連携・解除等の操作には管理者権限が必要です。</span>
        </div>
      )}

      <div className="connections-grid">
        {brandConfig.features.amazonIntegration && (
          <>
            <div className="connection-card">
              <div className="connection-logo">
                <img src="/logos/amazon_seller_connection_logo.png" alt="Amazon Seller Partner" />
              </div>
              <div className="connection-header">
                <h2>Amazon Seller Partner API</h2>
                {amazonSellerConnected ? (
                  <Label variant="green">接続済み</Label>
                ) : (
                  <Label variant="gray">未接続</Label>
                )}
              </div>
              <p className="connection-description">
                Amazon Seller Centralアカウントと連携して、商品情報を取得できます
              </p>
              <Button
                variant="muted"
                size="md"
                onClick={() => navigate('/connections/amazon')}
              >
                接続を管理
              </Button>
            </div>

            <div className="connection-card">
              <div className="connection-logo">
                <img src="/logos/amazon_ads_connection_logo.png" alt="Amazon広告" />
              </div>
              <div className="connection-header">
                <h2>Amazon広告API</h2>
                {amazonAdsConnected ? (
                  <Label variant="green">接続済み</Label>
                ) : (
                  <Label variant="gray">未接続</Label>
                )}
              </div>
              <p className="connection-description">
                Amazon Advertising APIと連携して、スポンサーディスプレイ広告などのクリエイティブを入稿できます
              </p>
              <Button
                variant="muted"
                size="md"
                onClick={() => navigate('/connections/amazon-ads')}
              >
                接続を管理
              </Button>
            </div>
          </>
        )}

        <div className="connection-card">
          <div className="connection-logo">
            <img src="/logos/google_ads_connection_logo.png" alt="Google 広告" />
          </div>
          <div className="connection-header">
            <h2>Google 広告 API</h2>
            {googleAdsQuery.data?.connected ? (
              <Label variant="green">接続済み</Label>
            ) : (
              <Label variant="gray">未接続</Label>
            )}
          </div>
          <p className="connection-description">
            Google 広告と連携して、生成した素材をアセットライブラリに直接アップロードできます
          </p>
          <Button
            variant="muted"
            size="md"
            onClick={() => navigate('/connections/google-ads')}
          >
            接続を管理
          </Button>
        </div>

        {brandConfig.features.amazonIntegration && (
          <div className="connection-card disabled">
            <div className="connection-logo">
              <span className="connection-logo-fallback">LOGO</span>
            </div>
            <div className="connection-header">
              <h2>楽天API</h2>
              <Label variant="red">未実装</Label>
            </div>
            <p className="connection-description">
              楽天市場の商品情報を取得できます（今後実装予定）
            </p>
            <Button variant="muted" size="md" disabled>
              準備中
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
